import {useState, useEffect,} from 'react';
import {Accordion, Link, Paper, AccordionDetails, AccordionSummary , TableBody, Divider, TableContainer, TableCell,  Typography, Box, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Table, TableHead, TableRow } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useErrorBoundary } from 'react-error-boundary';
import SpecialElectionReportContent from '../../../types/reports/specialElectionReportContent';
import CheckIcon from '@mui/icons-material/Check';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

const SpecialElections = (): JSX.Element => {
  
  document.title = 'OCPF - Special Elections Reports';

  //either current year or next year depending on month
  const [reportYear, setReportYear] = useState((new Date().getMonth()<=9?new Date().getFullYear(): new Date().getFullYear()+1).toString());
  const [specialElectionReports, setSpecialElectionReports] = useState<SpecialElectionReportContent[]>([]);
  const { showBoundary } = useErrorBoundary(); 
  
  const years = Array.from(new Array(new Date().getFullYear()-2019), (val, index) => new Date().getFullYear() - index);

  useEffect(() => {
    getSpecialElectionReports();
  }, [reportYear]);
  
  const getSpecialElectionReports = async () => {

    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/specialelections/${reportYear}`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get special election reports.'));
        }
        return result.json();
        
      }).then(result => {
        setSpecialElectionReports(result); 
      });
    }catch(error){
        console.error('Uncaught Error:', error);
    }
  }

  const handleChange = (event: SelectChangeEvent) => {
    setReportYear(event.target.value);
  };


    return (
      <Main>
        <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            gutterBottom
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            marginBottom={2}
            >
            Special Elections
        </Typography>
        <Divider/>
        <Accordion defaultExpanded>
          <AccordionSummary key='special-election-def' expandIcon={<ArrowDropDownIcon />}>
            <Typography variant='h6' component='h6'>What is a Special Election?</Typography>
          </AccordionSummary>
          <AccordionDetails>  
       
        <Typography variant='body1' component='p'>
          Special elections are held to fill vacancies in House and Senate seats. Elections are called by the respective chambers, with the filing dates for nomination papers subsequently set by the Secretary of the Commonwealth. OCPF sets the campaign finance reporting schedule after notification by the Secretary.
        </Typography>
        <Typography component='span'>
        Candidates in special legislative elections have three reporting periods for the election cycle:
          <ul>
            <li key='preprimary'>
            A <b>Pre-Primary reporting period</b>: deposit reports are due for each deposit made between the pre-primary period's starting date and ending date (18 days before the primary);
            </li>
            <li key='preelection'>
            A <b>Pre-Election period</b>: deposit reports are due 8 days before the special election, starting the day after the pre-primary ending date through 10 days prior to the due date (18 days before the special election); and
            </li>
            <li key='postelection'>
            A <b>Post-Election period</b>: deposit reports are due 30 days after the special election and complete through 10 days prior to the due date (20 days after the special election).
            </li>
          </ul>
        </Typography>
        <Typography variant='body1' component='p'>
        Expenditures made by the candidate are reported by the candidate's designated depository bank by the 5th of each month, covering the entire previous month, i.e. January's report is due by February 5th and covers January 1st through the 31st.
        </Typography>
        <Typography marginTop={1} variant='body1' component='p'>
        If a candidate maintains a committee or has money or liabilities outstanding after the Post-Election report, he or she must file a year-end report covering the subsequent activity as well as in-kind contributions received through this election cycle.
          </Typography>
    
            </AccordionDetails>
        </Accordion>

        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
        <InputLabel id="year-id">Report Year</InputLabel>
        <Select
          labelId="year-id"
          id="year-select"
          aria-labelledby='year-id'
          value={reportYear}
          onChange={handleChange}
          label="Report Year"
        >
          {years.map((year, index) => (
            
            <MenuItem key={year} value={year}>{year}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
           
            { specialElectionReports.length > 0 ?
              specialElectionReports.map((report, index) => ( 
                <Box component={Paper} sx={{padding: 2,}} key={'special-election-box-' + index} m={2} marginBottom={5}>
                <Typography variant='h6' component='h6' marginTop={2}>{report.generalElectionYear} {report.office}: {report.districtDescription}</Typography>
                <Divider/>
                <Typography marginTop={1}><i>{report.coverage}</i></Typography>
                <Typography marginTop={1}>{report.reason}</Typography>
                <Box sx={{borderLeft: '5px solid #f5f5f5', marginTop:2, marginBottom:2}}>
                  <Typography  marginLeft={2}><b>Special Primary Election: {new Date(report.primaryElectionDate).toLocaleDateString(undefined,{
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    } )}</b>
                  </Typography>
                  <Typography  marginLeft={2}><b>Special (Final) Election: {new Date(report.generalElectionDate).toLocaleDateString(undefined,{
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    } )}</b>
                  </Typography>
                  
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow key={report.districtCode + '-' + report.generalElectionYear + '-' + (index + 1)}>
                        <TableCell></TableCell>
                        <TableCell align='center'><b>Reporting Period</b></TableCell>
                        <TableCell align='center'><b>Due Date</b></TableCell>
                        <TableCell align='center'><b>Late Contribution Reporting Period</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow  key={report.districtCode + '-' + report.generalElectionYear + '-' + (index + 2)}>  
                        <TableCell component="th" scope="row">Pre-Primary Period</TableCell>
                        <TableCell align='center'>{report.prePrimaryStartDate + ' through ' + report.prePrimaryEndDate}</TableCell>
                        <TableCell align='center'>{report.prePrimaryDueDate}</TableCell>
                        <TableCell align='center'>{report.prePrimaryLcStartDate + ' through ' + report.prePrimaryLcEndDate}</TableCell>
                      </TableRow>
                      <TableRow  key={report.districtCode + '-' + report.generalElectionYear + '-' + (index + 3)}>  
                        <TableCell component="th" scope="row">Pre-Election Period</TableCell>
                        <TableCell align='center'>{report.preElectionStartDate + ' through ' + report.preElectionEndDate}</TableCell>
                        <TableCell align='center'>{report.preElectionDueDate}</TableCell>
                        <TableCell align='center'>{report.preElectionLcStartDate + ' through ' + report.preElectionLcEndDate}</TableCell>
                      </TableRow>
                      <TableRow  key={report.districtCode + '-' + report.generalElectionYear + '-' + (index + 4)}>  
                        <TableCell component="th" scope="row">Post-Election Period</TableCell>
                        <TableCell align='center'>{report.postElectionStartDate + ' through ' + report.postElectionEndDate}</TableCell>
                        <TableCell align='center'>{report.postElectionDueDate}</TableCell>
                        <TableCell align='center'>-</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  </TableContainer>
                <Box sx={{borderLeft: '5px solid #f5f5f5', marginBottom:2, marginTop: 2}}>
                <Typography marginLeft={2}>
                  <b>
                  Totals include reported activity with dates between the beginning of the year and the post-election period's ending date. Expenditures are reported by the candidate's depository bank. Contributions are reported by the candidate via deposit reports. In-kind contributions are not included in the total until a year-end report covering the period is filed.
                  </b>
                  </Typography>
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow key={report.districtCode + '-' + report.generalElectionYear + '-candidates'}>
                          <TableCell><b>Candidate</b></TableCell>
                          <TableCell><b>Party Affiliation</b></TableCell>
                          <TableCell><b>Winner?</b></TableCell>
                          <TableCell><b>Contributions reported to date</b></TableCell>
                          <TableCell><b>Expenditures reported to date</b></TableCell>
                          <TableCell><b>Cash On Hand</b></TableCell>
                        </TableRow>
                       </TableHead>
                       <TableBody>
                          {report.candidates.map((candidate, index) => (
                            <TableRow key={report.districtCode + '-' + report.generalElectionYear + '-' + candidate.fullNameReverse}>
                              <TableCell><Link href={'/filers?q=' + candidate.cpfId} target='_blank'>{candidate.cpfId + ' ' +candidate.fullNameReverse}</Link></TableCell>
                              <TableCell>{candidate.partyAffiliation}</TableCell>
                              <TableCell>{candidate.isWinner ? <CheckIcon/> : <></>}</TableCell>
                              <TableCell><Link href={`/filers?q=${candidate.cpfId}&section=Contributions`}>{candidate.totals.depositReceiptsToDate}</Link></TableCell>
                              <TableCell><Link href={`/filers?q=${candidate.cpfId}&section=Expenditures`}>{candidate.totals.bankExpendituresToDate}</Link></TableCell>
                              <TableCell>
                                {
                                  candidate.totals.lastFiledBankReportId > 0 &&
                                  <>
                                    <Link target="_blank" href={"/reports/displayreport?id=" + candidate.totals.lastFiledBankReportId}>{candidate.totals.lastFiledBankReportEndBalance}</Link>{" (as of " +candidate.totals.lastFiledBankReportEndDate + ")"}
                                  </>
                                }
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                </Box>
              ))
            : <Typography variant='h6' component='h6'>No special elections were scheduled for {reportYear}.</Typography>
            }

       
      </Box>
       
    </Container>
    </Main>
  );
};

export default SpecialElections;
